import { create } from 'zustand';

import { IS_DEV_BUILD } from '../api/env';
import { hasUpdate, isBoardPath, navigationBoundary, parseBuildInfo, shouldPrompt, updateMode, type BuildInfo, type UpdateBoundary, type UpdateMode } from './version';

export const CURRENT_BUILD: BuildInfo = typeof __APP_BUILD__ === 'undefined' ? { version: 'dev', builtAt: '' } : __APP_BUILD__;

export const UPDATE_MODE: UpdateMode = typeof window === 'undefined' ? 'dev' : updateMode(IS_DEV_BUILD, window.location);

export type UpdateStatus = 'idle' | 'checking' | 'current' | 'available' | 'failed';

export interface UpdateState {
  status: UpdateStatus;
  remote: BuildInfo | null;
  /** version the teacher answered "Để sau" for */
  dismissed: string | null;
  prompting: boolean;
  check: () => Promise<BuildInfo | null>;
  dismiss: () => void;
}

let inflight: Promise<BuildInfo | null> | null = null;

async function fetchRemoteBuild(): Promise<BuildInfo | null> {
  try {
    // the query defeats any cache on WebViews too old to honour cache: 'no-store'
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    return parseBuildInfo(await response.json());
  } catch {
    return null;
  }
}

export const useUpdateStore = create<UpdateState>((set, get) => ({
  status: 'idle',
  remote: null,
  dismissed: null,
  prompting: false,
  check() {
    if (UPDATE_MODE !== 'remote') return Promise.resolve(null);
    if (inflight) return inflight;
    set({ status: 'checking' });
    inflight = fetchRemoteBuild().then((remote) => {
      inflight = null;
      if (!remote) {
        set({ status: 'failed' });
        return null;
      }
      set({ remote, status: hasUpdate(CURRENT_BUILD, remote) ? 'available' : 'current' });
      return remote;
    });
    return inflight;
  },
  dismiss() {
    const remote = get().remote;
    set({ prompting: false, dismissed: remote ? remote.version : null });
  },
}));

export function reloadToUpdate(): void {
  window.location.reload();
}

interface RouterLike {
  state: { location: { pathname: string } };
  subscribe: (listener: (state: { location: { pathname: string } }) => void) => () => void;
}

/** Checks at natural boundaries only - no polling, nothing during a lesson. */
export function installUpdateChecks(router: RouterLike): void {
  if (UPDATE_MODE !== 'remote') return;
  const pathname = () => router.state.location.pathname;

  async function atBoundary(boundary: UpdateBoundary) {
    if (isBoardPath(pathname())) return;
    const remote = await useUpdateStore.getState().check();
    const state = useUpdateStore.getState();
    if (state.prompting) return;
    const dialogOpen = document.querySelector('.modal-layer') !== null;
    if (shouldPrompt({ boundary, pathname: pathname(), current: CURRENT_BUILD, remote, dismissed: state.dismissed, dialogOpen })) {
      useUpdateStore.setState({ prompting: true });
    }
  }

  let previous = pathname();
  router.subscribe((state) => {
    const next = state.location.pathname;
    if (next === previous) return;
    const boundary = navigationBoundary(previous, next);
    previous = next;
    if (next !== '/') useUpdateStore.setState({ prompting: false });
    if (boundary) void atBoundary(boundary);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void atBoundary('foreground');
  }, false);
}
